import { FeaturedInitialFormData } from "@saleor/categories/components/CategoryFeaturedCheckbox";
import DeleteFilterTabDialog from "@saleor/components/DeleteFilterTabDialog";
import SaveFilterTabDialog, {
  SaveFilterTabDialogFormData
} from "@saleor/components/SaveFilterTabDialog";
import {
  DEFAULT_INITIAL_PAGINATION_DATA,
  DEFAULT_INITIAL_SEARCH_DATA
} from "@saleor/config";
import CouponViewMallsDialog from "@saleor/coupons/components/CouponViewMallsDialog/CouponViewMallsDialog";
import { useCouponToggleFeature } from "@saleor/coupons/mutations";
import useBulkActions from "@saleor/hooks/useBulkActions";
import useListSettings from "@saleor/hooks/useListSettings";
import useNavigator from "@saleor/hooks/useNavigator";
import useNotifier from "@saleor/hooks/useNotifier";
import usePaginator, {
  createPaginationState
} from "@saleor/hooks/usePaginator";
import useShop from "@saleor/hooks/useShop";
import useUser from "@saleor/hooks/useUser";
import { useMallListQuery } from "@saleor/malls/queries";
import { getStringOrPlaceholder, maybe } from "@saleor/misc";
import useAssignedMallsSearch from "@saleor/searches/useAssignedMallsSearch";
import useMallSearch from "@saleor/searches/useMallSearch";
import { ListViews } from "@saleor/types";
import {
  CouponDealTypeEnum,
  CouponSortField,
  OrderDirection,
  SmopStatusEnum
} from "@saleor/types/globalTypes";
import createDialogActionHandlers from "@saleor/utils/handlers/dialogActionHandlers";
import createFilterHandlers from "@saleor/utils/handlers/filterHandlers";
import React, { useEffect, useState } from "react";

import { CouponListPage } from "../../components/CouponListPage";
import {
  useCouponListQuery,
  useCouponMallsListQuery,
  useInitialAllMallsFilterDataQuery,
  useInitialAssignedMallsFilterDataQuery
} from "../../queries";
import {
  couponAddUrl,
  couponListUrl,
  CouponListUrlDialog,
  couponRankingListUrl,
  couponRedemptionListUrl,
  CouponsListUrlFilters,
  CouponsListUrlQueryParams,
  couponUrl
} from "../../url";
import {
  areFiltersApplied,
  deleteFilterTab,
  getActiveFilters,
  getFilterOpts,
  getFilterQueryParam,
  getFilterTabs,
  getFilterVariables,
  saveFilterTab
} from "./filters";

interface CouponListProps {
  params: CouponsListUrlQueryParams;
}

export const CouponList: React.FC<CouponListProps> = ({ params }) => {
  const navigate = useNavigator();
  const paginate = usePaginator();
  const shop = useShop();
  const notify = useNotifier();
  const [mallQueryAfterPagination, setMallQueryAfterPagination] = useState("");
  const [allMallList, updateAllMallList] = useState([]);
  const [stopQuery, setStopQuery] = useState(false);
  const [couponMallsList, setCouponMallsList] = useState([]);
  const [couponId, setCouponId] = useState("");
  const { user } = useUser();
  const { reset: resetSelected } = useBulkActions(params.ids);

  const currencySymbol = shop?.defaultCurrency || "PHP";
  const { updateListSettings, settings } = useListSettings(
    ListViews.COUPON_LIST
  );

  const paginationState = createPaginationState(settings?.rowNumber, params);
  const DEFAULT_TAB_COUNT = 1;
  const mallArray = params.mall !== undefined ? Object.values(params.mall) : [];

  const couponTabs = [
    {
      data: {
        dealType: CouponDealTypeEnum.DEALS
      },
      name: "Mall Deals"
    },
    {
      data: {
        dealType: CouponDealTypeEnum.EVENTS
      },
      name: "Mall Events"
    },
    ...getFilterTabs()
  ];

  const dealType = React.useMemo(() => {
    const currentTabIndex = parseInt(params.activeTab || "0", 10);
    return couponTabs[currentTabIndex]?.data.dealType ===
      CouponDealTypeEnum.DEALS
      ? CouponDealTypeEnum.DEALS
      : CouponDealTypeEnum.EVENTS;
  }, [params.activeTab]);

  const queryVariables = React.useMemo(
    () => ({
      ...paginationState,
      filter: {
        ...getFilterVariables(params),
        dealType: [dealType]
      },
      sortBy: {
        direction: OrderDirection.DESC,
        field: CouponSortField.START_DATE
      }
    }),
    [paginationState, params, settings.rowNumber, dealType]
  );

  const { data, loading, refetch } = useCouponListQuery({
    displayLoader: true,
    variables: queryVariables
  });

  React.useEffect(() => {
    refetch();
  }, [settings.rowNumber]);

  const {
    data: couponMallsListQuery,
    loading: couponMallsListQueryLoading,
    refetch: refetchCouponMallsListQuery
  } = useCouponMallsListQuery({
    skip: couponId === "",
    variables: {
      first: 100,
      id: couponId
    }
  });

  const {
    data: mallQuery,
    loading: mallListLoading,
    refetch: refetchMallList
  } = useMallListQuery({
    skip: stopQuery,
    variables: {
      after: mallQueryAfterPagination,
      filter: {
        status: SmopStatusEnum.ACT
      },
      first: 100
    }
  });

  const mallNextPageQuery = mallQuery?.malls?.pageInfo;
  useEffect(() => {
    if (mallQuery && !mallListLoading) {
      updateAllMallList(current => [
        ...current,
        ...(mallQuery?.malls.edges?.map(edge => edge.node?.name) ?? [])
      ]);
      setMallQueryAfterPagination(mallNextPageQuery.endCursor);

      if (
        !mallNextPageQuery.hasNextPage &&
        mallNextPageQuery?.endCursor === null
      ) {
        setMallQueryAfterPagination("");
        setStopQuery(true);
      } else if (mallQuery?.malls.pageInfo?.endCursor) {
        refetchMallList();
      }
    }
  }, [
    mallQuery,
    mallListLoading,
    updateAllMallList,
    refetchMallList,
    mallNextPageQuery
  ]);

  useEffect(
    () =>
      navigate(
        couponListUrl({
          ...params,
          ...DEFAULT_INITIAL_PAGINATION_DATA
        }),
        true
      ),
    [settings?.rowNumber]
  );

  useEffect(() => {
    if (couponId) {
      const couponMalls = maybe(() =>
        couponMallsListQuery?.coupon.malls.edges.map(edges => edges.node.name)
      );
      setCouponMallsList(couponMalls);
    } else if (params.action === "view-malls" && couponId === "") {
      closeModal();
    }
  }, [couponMallsListQuery]);

  const currentTab =
    params.activeTab === undefined
      ? areFiltersApplied(params)
        ? couponTabs.length
        : 0
      : parseInt(params.activeTab, 0);

  const [changeFilters, resetFilters] = createFilterHandlers({
    createUrl: couponListUrl,
    getFilterQueryParam,
    navigate,
    params
  });

  const [openModal, closeModal] = createDialogActionHandlers<
    CouponListUrlDialog,
    CouponsListUrlQueryParams
  >(navigate, couponListUrl, params);

  const handleTabChange = (tab: string | number) => {
    resetSelected();
    navigate(
      couponListUrl({
        ...params,
        activeTab: tab.toString(),
        ...couponTabs[tab].data,
        after: null,
        before: null
      })
    );
  };

  const changeFilterField = (filter: CouponsListUrlFilters) => {
    resetSelected();
    navigate(
      couponListUrl({
        ...getActiveFilters(params),
        ...filter,
        activeTab: params.activeTab
      })
    );
  };

  const handleFilterTabDelete = () => {
    const currentCustomFilterTabIndex = currentTab - DEFAULT_TAB_COUNT;
    deleteFilterTab(currentCustomFilterTabIndex + 1);
  };

  const handleTabSave = (data: SaveFilterTabDialogFormData) => {
    saveFilterTab(data.name, getActiveFilters(params));
    handleTabChange(couponTabs.length + 1);
  };

  const { loadNextPage, loadPreviousPage, pageInfo } = paginate(
    maybe(() => data.coupons.pageInfo),
    paginationState,
    params
  );

  const [updateIsFeatured] = useCouponToggleFeature({
    onCompleted: event => {
      const { errors } = event.couponToggleIsFeatured;
      if (errors.length > 0) {
        notify({
          status: "error",
          text: errors[0].message
        });
      }
      refetch();
    }
  });

  const handleSubmit = async (data: FeaturedInitialFormData) => {
    updateIsFeatured({
      variables: { id: data.id }
    });
    return null;
  };

  const searchAssignedMall = useAssignedMallsSearch({
    variables: {
      ...DEFAULT_INITIAL_SEARCH_DATA,
      filterStatus: SmopStatusEnum.ACT,
      first: 5
    }
  });

  const searchAllMall = useMallSearch({
    variables: {
      ...DEFAULT_INITIAL_SEARCH_DATA,
      filterStatus: "ACT",
      first: 5
    }
  });

  const {
    data: initialAssignedMallFilterData
  } = useInitialAssignedMallsFilterDataQuery({
    variables: {
      mall: mallArray
    }
  });

  const { data: initialAllMallFilterData } = useInitialAllMallsFilterDataQuery({
    variables: {
      mall: mallArray
    }
  });

  const handlefilterOptsChange = () => {
    let filterOpts;
    if (initialAssignedMallFilterData?.me.malls.edges.length > 0) {
      filterOpts = getFilterOpts(params, {
        allMall: false,
        initialAssigned: maybe(
          () =>
            initialAssignedMallFilterData.me.malls.edges.map(
              mall => mall.node
            ) || []
        ),
        searchAssigned: searchAssignedMall
      });
    } else {
      filterOpts = getFilterOpts(params, {
        allMall: true,
        initialAllMalls: maybe(
          () =>
            initialAllMallFilterData.malls.edges.map(edge => edge.node) || []
        ),
        searchAllMall
      });
    }
    return filterOpts;
  };

  const filterOptsValue = handlefilterOptsChange();

  const handleOpenViewMallList = id => {
    setCouponId(id);
    refetchCouponMallsListQuery();
    openModal("view-malls");
  };

  const handleCloseViewMallList = () => {
    setCouponMallsList([]);
    closeModal();
  };

  const userPermissions = user.userPermissions;

  return (
    <>
      <CouponListPage
        userPermissions={userPermissions}
        brands={null}
        activeTab={params.activeTab}
        currencySymbol={currencySymbol}
        currentTab={currentTab}
        coupons={maybe(() => data?.coupons.edges.map(edge => edge.node), [])}
        filterOpts={filterOptsValue}
        initialSearch={params.query || ""}
        onSearchChange={query => changeFilterField({ query })}
        onFilterChange={filter => changeFilters(filter, true)}
        onAll={resetFilters}
        onTabChange={handleTabChange}
        onTabDelete={() => openModal("delete-search")}
        onTabSave={() => openModal("save-search")}
        tabs={couponTabs.map(tab => tab.name)}
        settings={settings}
        disabled={loading}
        onNextPage={loadNextPage}
        onPreviousPage={loadPreviousPage}
        onRowClick={id => () => navigate(couponUrl(id))}
        onAdd={() => navigate(couponAddUrl())}
        onUpdateListSettings={updateListSettings}
        pageInfo={pageInfo}
        onClickCouponRedemption={id => () =>
          navigate(couponRedemptionListUrl(id))}
        onSubmit={handleSubmit}
        navigateToRanking={() => navigate(couponRankingListUrl())}
        onViewMalls={id => () => handleOpenViewMallList(id)}
      />
      <SaveFilterTabDialog
        open={params.action === "save-search"}
        confirmButtonState="default"
        onClose={closeModal}
        onSubmit={handleTabSave}
      />
      <DeleteFilterTabDialog
        open={params.action === "delete-search"}
        confirmButtonState="default"
        onClose={closeModal}
        onSubmit={handleFilterTabDelete}
        tabName={getStringOrPlaceholder(couponTabs[currentTab]?.name)}
      />
      <CouponViewMallsDialog
        couponMallsList={couponMallsList}
        allMallsList={allMallList}
        loading={couponMallsListQueryLoading}
        onClose={handleCloseViewMallList}
        open={params.action === "view-malls"}
      />
    </>
  );
};
export default CouponList;
